import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Paper, Stack, Typography } from '@mui/material'
import { TooltipProps } from 'recharts'
import { TariffModel } from '../../../models/tariffs-model'
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { getCurrencyValue } from 'src/Libs/e-mobility/shared'
import { getSubunitOfCurrency } from '../../../utils/get-subunit-of-currency'
import { formatYTicks } from 'src/Libs/dynamic-power-prices/utils'
import { format } from '@e3dc-react/shell/Libs/date'

interface Props extends TooltipProps<ValueType, NameType> {
  priceComponent: TariffModel['dynamicPrices'][0] | undefined
  currencyCode: string
}

/**
 * Dynamic tariffs chart tooltip component.
 * Props interface overridden because of custom behavior. The data to be shown
 * is NOT read from the payload of the point which triggered the tooltip, but is instead
 * received as prop from the parent component which handles the custom tooltip behavior.
 *
 * @param - Tooltip props from recharts lib
 */
export const TariffChartTooltip: React.FC<Props> = ({ active, priceComponent, currencyCode }): React.ReactElement => {
  const { t } = useCustomTranslation()
  const { subunit } = getSubunitOfCurrency(currencyCode)

  if (!priceComponent || !active) {
    return <Typography variant="overline">{t('common.noDataShort')}</Typography>
  }

  const localizedPrice = getLocalizedNumber(priceComponent.gridPrice)

  return (
    <Paper sx={theme => ({ padding: theme.spacing(2), borderRadius: theme.spacing(1) })}>
      <Stack flexDirection={'column'}>
        <Typography fontSize={14} fontWeight={500} mb={0}>
          {format(new Date(priceComponent.startDateTime ?? 0), t('common.format.date-fns-date'))}
        </Typography>
        <Typography fontSize={14} fontWeight={500}>{`${format(new Date(priceComponent.startDateTime ?? 0), 'HH:mm')} - ${format(
          new Date(priceComponent.endDateTime ?? 0),
          'HH:mm'
        )}`}</Typography>
        <Typography fontSize={14} fontWeight={700} mt={1}>
          {subunit ? formatYTicks(localizedPrice, subunit) : getCurrencyValue(priceComponent.gridPrice, currencyCode)}
        </Typography>
      </Stack>
    </Paper>
  )
}
